import React, { useCallback, useState, useEffect } from "react";
import { Input, Button, Form, Alert } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import LoginLayout from "components/layouts/login-layout/login-layout";
import GoogleAuthButton from "components/google-auth-button/google-auth-button";
import routeNames from "constants/route-names";
import regex from "constants/regex";
import { GoogleAuthStatus } from "constants/google-auth-status";
import { useAuth } from "context/AuthContext";
import { getPostLoginPath, getRememberedPath } from "utils/auth-redirect";

const LoginScreen = () => {
  const [searchParams] = useSearchParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [displayLoginError, setDisplayLoginError] = useState(false);
  const [isMagicLinkMode, setIsMagicLinkMode] = useState(false);
  const [sentMagicLink, setSentMagicLink] = useState(false);
  // While Google is resolving a brand new account (or linking to an existing password one),
  // the email/password form underneath is hidden: it is not a second way to finish that flow.
  const [googleAuthStatus, setGoogleAuthStatus] = useState<GoogleAuthStatus>(GoogleAuthStatus.IDLE);
  const isGoogleAuthActive = googleAuthStatus !== GoogleAuthStatus.IDLE;

  const { login, sendMagicLink, verifyMagicLink } = useAuth();

  useEffect(() => {
    const emailparam: string | null = searchParams.get("emailparam");
    const mode: string | null = searchParams.get("mode");

    if (mode === "signIn" && emailparam && verifyMagicLink) {
      verifyMagicLink(location.href, emailparam);
    }
  }, [searchParams]);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleLogin = useCallback(async () => {
    if (login) {
      const successLogin = await login(
        {
          email,
          password,
        },
        getPostLoginPath(search)
      );

      if (successLogin) {
        setDisplayLoginError(false);
      } else {
        setDisplayLoginError(true);
      }
    }
  }, [email, password, search]);

  const handleMagicLink = useCallback(async () => {
    if (sendMagicLink && email) {
      const successLogin = await sendMagicLink(email, getRememberedPath(search));

      if (successLogin) {
        setDisplayLoginError(false);
        setSentMagicLink(true);
      } else {
        setDisplayLoginError(true);
      }
    }
  }, [sendMagicLink, email, search]);

  if (isMagicLinkMode && sentMagicLink) {
    return (
      <LoginLayout title="" className="login-screen">
        <>
          <div className="link-label forgot-password-link">We’ve just emailed you a magic link to sign in.</div>
          <span className="link-label">
            Trouble Signing In?
            <a
              className="inline-link"
              style={{ fontWeight: 500 }}
              href="mailto:support@highnotes.com?subject=Trouble Signing In"
            >
              Contact Us
            </a>
          </span>
        </>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout title={isMagicLinkMode ? "Sign In with a one-time Magic Link" : ""} className="login-screen">
      <>
        {!isMagicLinkMode && (
          <GoogleAuthButton
            redirectTo={getPostLoginPath(search)}
            onStatusChange={setGoogleAuthStatus}
            showDivider={!isGoogleAuthActive}
          />
        )}

        {!isGoogleAuthActive && (
          <>
            <Form
              className="login-form"
              autoComplete="off"
              onFinish={isMagicLinkMode ? handleMagicLink : handleLogin}
            >
              <Form.Item
                name="email"
                className="login-form-item"
                rules={[
                  { required: true, message: "Please input your Email." },
                  {
                    pattern: regex.email,
                    message: "Please provide a valid email address",
                  },
                ]}
                required
                validateTrigger="onBlur"
              >
                <Input
                  name="email"
                  placeholder="Email"
                  className="app-input"
                  value={email}
                  autoComplete="off"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(event.target.value);
                  }}
                  allowClear
                />
              </Form.Item>

              {!isMagicLinkMode && (
                <Form.Item
                  name="password"
                  className="login-form-item"
                  rules={[{ required: true, message: "Please input a password." }]}
                  required
                  validateTrigger="onBlur"
                >
                  <Input.Password
                    name="password"
                    placeholder="Password"
                    className="app-input"
                    value={password}
                    autoComplete="new-password"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setPassword(event.target.value);
                    }}
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
              )}

              {displayLoginError ? (
                <Alert
                  message="The email or password is incorrect."
                  className="login-error"
                  type="error"
                  showIcon
                />
              ) : null}

              <Button className="login-button" htmlType="submit">
                {isMagicLinkMode ? "Send Magic Link" : "Sign in"}
              </Button>
            </Form>
            <div className="login-links-container">
              {isMagicLinkMode ? (
                <div className="link-label forgot-password-link">We will email you a magic link to sign in.</div>
              ) : (
                <Link
                  to={{
                    pathname: routeNames.FORGOT_PASSWORD,
                  }}
                  className="link-label forgot-password-link"
                  rel="noopener"
                >
                  Forgot your password?
                </Link>
              )}

              {!isMagicLinkMode && (
                <a
                  className="link-label"
                  onClick={() => setIsMagicLinkMode(!isMagicLinkMode)}
                >
                  Sign in with a one-time magic link
                </a>
              )}

              {isMagicLinkMode && (
                <span className="link-label">
                  Trouble Signing In?
                  <a
                    className="inline-link bold-link"
                    href="mailto:support@highnotes.com?subject=Trouble Signing In"
                  >
                    Contact Us
                  </a>
                </span>
              )}
            </div>

            {!isMagicLinkMode && (
              <Button className="login-create-account-button" onClick={() => navigate(routeNames.SIGNUP)}>
                Create your account
              </Button>
            )}
          </>
        )}
      </>
    </LoginLayout>
  );
};

export default LoginScreen;
